import React, { useContext } from "react";

import { css } from "@emotion/react";

import { DataContext } from "../../../../../providers/PelletsCheckoutDataProvider";
import { DataContext as CalculatorContext } from "../../../../../providers/PelletsDataProvider";
import { toReadableDate } from "../../../../../utils/DateHelpers";
import StorageHandler, {
    storageSession as session,
} from "../../../../../utils/StorageHandler";
import resolveWebView from "../../../../../utils/webViewRedirect";
import { navigate } from "../../../../shared/LinkComponent";
import Margin from "../../../../shared/Margin";
import Headline from "../../../../shared/NewHeadline";
import OrderChangableDetail from "../../../../shared/OrderChangableDetail";
import ContactData from "../StepDeliveryAddress/ContactData";
import DeliveryAddress from "../StepDeliveryAddress/DeliveryAddress";
import InvoiceAddress from "../StepDeliveryAddress/InvoiceAddress";
import Comment from "../StepDeliveryDetails/Comment";
import DeliveryTime from "../StepDeliveryDetails/DeliveryTime";
import Hose from "../StepDeliveryDetails/Hose";
import PromotionCode from "../StepDeliveryDetails/PromotionCode";
import StepPaymentType from "../StepPaymentType";
import Agb from "./Agb";
import Amount from "./Amount";
import Newsletter from "./Newsletter";
import * as styles from "./style.module.scss";

const StepOverview = () => {
    const storage = new StorageHandler();
    const dataContext = useContext(DataContext);
    const calculatorContext = useContext(CalculatorContext);
    const prices = dataContext.actions.getPrices();
    const {
        amount,
        deliveryAddressData,
        orderComment,
        deliveryTime,
        paymentType,
        deliveryTimeWishDate,
        deliveryTimeFrame,
        hose,
        useInvoiceAddress,
        invoiceAddress,
    } = dataContext.data;
    const { setEditMode, setOverlayContent } = dataContext.actions;
    const { additions } = calculatorContext.data;

    /**
     * Get the current payment type.
     */
    const getCurrentPaymentType = () => {
        const currentKey = Object.keys(additions.payment_types).filter(
            (key) => additions.payment_types[key].id == paymentType
        );
        return additions.payment_types[currentKey].name;
    };

    /**
     * Returns the label for delivery time
     */
    const getDeliveryTimeLabel = () => {
        if (deliveryTimeWishDate !== "") {
            return `Wunschtermin: ${toReadableDate(deliveryTimeWishDate)}`;
        }
        if (deliveryTimeFrame !== "") {
            return `${additions.delivery_times[deliveryTime].name}, ${additions.time_frames[deliveryTimeFrame].name}`;
        }
        return additions.delivery_times[deliveryTime].name;
    };

    /**
     * Returns the label for the amount.
     */
    const getAmountLabel = () => {
        const product = storage.get("pel:order-product-type");
        const value =
            product === "loose"
                ? "kg"
                : product === "big-bag"
                ? " Säcke"
                : " Paletten";

        return `Menge: ${amount}${value}`;
    };

    return (
        <Margin Component="section" top="10">
            <Headline>Bestelldetails</Headline>
            <OrderChangableDetail
                label={getAmountLabel()}
                changeContent={<Amount />}
                onOverlayInit={() => {
                    setOverlayContent("menge");
                }}
                gtag="pel:checkout:overview:amount"
                DataContext={DataContext}
            />
            {additions && (
                <OrderChangableDetail
                    label={getDeliveryTimeLabel()}
                    changeContent={<DeliveryTime />}
                    onOverlayInit={() => {
                        setOverlayContent("lieferdetails");
                    }}
                    gtag="pel:checkout:overview:delivery-time"
                    DataContext={DataContext}
                />
            )}
            {additions &&
                additions.hoses &&
                Object.keys(additions.hoses).length > 0 && (
                    <OrderChangableDetail
                        label={`Schlauchlänge: ${additions.hoses[hose].name}`}
                        changeContent={<Hose />}
                        onOverlayInit={() => {
                            setOverlayContent("lieferdetails");
                        }}
                        gtag="pel:checkout:overview:hose"
                        DataContext={DataContext}
                    />
                )}
            {additions && (
                <OrderChangableDetail
                    label={getCurrentPaymentType()}
                    changeContent={<StepPaymentType />}
                    onOverlayInit={() => {
                        setOverlayContent("zahlart");
                    }}
                    gtag="pel:checkout:overview:payment-type"
                    DataContext={DataContext}
                />
            )}
            <OrderChangableDetail
                label={orderComment ? orderComment : "Keine Bemerkung"}
                changeContent={<Comment />}
                onOverlayInit={() => {
                    setOverlayContent("lieferdetails");
                }}
                gtag="pel:checkout:overview:comment"
                DataContext={DataContext}
            />
            {session.get("pel:promotion-code") &&
                session.get("pel:promotion-code") !== "" && (
                    <>
                        <OrderChangableDetail
                            label={`Aktionscode: ${session.get(
                                "pel:promotion-code"
                            )}`}
                            changeContent={<PromotionCode />}
                            onOverlayInit={() => {
                                setOverlayContent("lieferdetails");
                            }}
                            gtag="pel:checkout:overview:promotion-code"
                            DataContext={DataContext}
                        />
                        {session.get("pel:promotion-code") &&
                            session.get("pel:promotion-code") !== "" && (
                                <p
                                    css={css`
                                        font-size: small;
                                    `}
                                >
                                    Bitte beachte: der Rabatt wird erst auf der
                                    Rechnung abgezogen.
                                </p>
                            )}
                    </>
                )}

            <OrderChangableDetail
                label="Kontaktdaten"
                value={
                    <div>
                        {deliveryAddressData.salutation}{" "}
                        {deliveryAddressData.firstname}{" "}
                        {deliveryAddressData.lastname}
                        {deliveryAddressData.email && (
                            <React.Fragment>
                                <br /> {deliveryAddressData.email}
                            </React.Fragment>
                        )}
                        {deliveryAddressData.phone && (
                            <React.Fragment>
                                <br /> {deliveryAddressData.phone}
                            </React.Fragment>
                        )}
                    </div>
                }
                changeContent={<ContactData />}
                onOverlayInit={() => {
                    setOverlayContent("lieferadresse");
                }}
                gtag="pel:checkout:overview:contact"
                DataContext={DataContext}
            />

            <OrderChangableDetail
                label="Lieferadresse"
                value={
                    <div>
                        {deliveryAddressData.street}{" "}
                        {deliveryAddressData.house_number}
                        <br />
                        {deliveryAddressData.zipcode} {deliveryAddressData.city}
                    </div>
                }
                onClick={() => {
                    setEditMode(true);
                    void navigate(
                        resolveWebView("/heizoel/bestellung/lieferadresse")
                    );
                }}
                changeContent={<DeliveryAddress />}
                onOverlayInit={() => {
                    setOverlayContent("lieferadresse");
                }}
                gtag="pel:checkout:overview:delivery-address"
                DataContext={DataContext}
            />

            {useInvoiceAddress && (
                <OrderChangableDetail
                    label="Rechnungsadresse"
                    value={
                        <div>
                            {invoiceAddress.salutation}{" "}
                            {invoiceAddress.firstname} {invoiceAddress.lastname}
                            <br />
                            {invoiceAddress.street}{" "}
                            {invoiceAddress.house_number}
                            <br />
                            {invoiceAddress.zipcode} {invoiceAddress.city}
                        </div>
                    }
                    changeContent={<InvoiceAddress />}
                    onOverlayInit={() => {
                        setOverlayContent("lieferadresse");
                    }}
                    gtag="hel:checkout:overview:invoice-address"
                    DataContext={DataContext}
                />
            )}

            <Agb />
            <Newsletter />
            {prices !== null && (
                <div className={styles.totalPrice}>
                    Gesamtpreis: {prices.priceTotalGross}&nbsp;€
                </div>
            )}
        </Margin>
    );
};

export default StepOverview;
